import { CacheEntities } from "./cache-types";

/**
 * Security configuration for cache keys - designed to prevent malicious input attacks
 */
const SECURITY_CONFIG = {
  // Maximum key length to prevent memory exhaustion
  MAX_KEY_LENGTH: 512,

  // Allowed key prefixes - keys must start with one of these
  ALLOWED_PREFIXES: Object.values(CacheEntities),

  // Pattern for safe key characters. Allow JSON characters since generateCacheKey creates them
  VALID_KEY_CHARACTERS: /^[a-zA-Z0-9:._/{}",-]+$/,

  // Explicitly forbidden patterns for security
  FORBIDDEN_PATTERNS: [
    /[\x00-\x1f\x7f]/, // Control characters
    /\*/, // Glob wildcards that could cause SCAN issues
    /\?/, // Glob wildcards
    /\[/, // Glob wildcards
    /\]/, // Glob wildcards
  ],
};

/**
 * Error thrown when cache key is invalid
 */
export class InvalidCacheKeyError extends Error {
  constructor(key: string, reason: string) {
    super(`Invalid cache key "${key}": ${reason}`);
    this.name = "InvalidCacheKeyError";
  }
}

/**
 * Validates a cache key to prevent malicious input attacks
 * @param key The cache key to validate
 * @returns The validated key (unchanged if valid)
 * @throws InvalidCacheKeyError if key contains malicious patterns
 */
export function validateCacheKey(key: string): string {
  // 1. Basic validation
  if (typeof key !== "string" || key.length === 0) {
    throw new InvalidCacheKeyError(key, "Key must be a non-empty string");
  }

  if (key.length > SECURITY_CONFIG.MAX_KEY_LENGTH) {
    throw new InvalidCacheKeyError(key, `Key too long`);
  }

  // 2. Check for dangerous patterns that could enable attacks
  for (const pattern of SECURITY_CONFIG.FORBIDDEN_PATTERNS) {
    if (pattern.test(key)) {
      throw new InvalidCacheKeyError(key, "Key contains forbidden pattern");
    }
  }

  // 3. Validate against safe character set
  if (!SECURITY_CONFIG.VALID_KEY_CHARACTERS.test(key)) {
    throw new InvalidCacheKeyError(key, "Key contains unsafe characters");
  }

  // 4. Validate prefix (business logic requirement)
  const hasValidPrefix = SECURITY_CONFIG.ALLOWED_PREFIXES.some((prefix) =>
    key.startsWith(prefix)
  );

  if (!hasValidPrefix) {
    throw new InvalidCacheKeyError(key, "Key starts with an invalid prefix");
  }

  return key;
}
